import React, { useCallback, useEffect, useState } from "react";
// componentes
import { Row, Col, Card, CardBody, CardTitle, Button } from "reactstrap";
import Ranking from "../ranking";
import Participants from "../Participant/Participants";
import DonutChart from "./DonutChart";
import FeedChart from "./FeedChart";
import PieChart from "./PieChart";
import RankChart from "./RankChart";
import Feed from "../Feed/Feed";
// alerta
import { toast } from "react-toastify";
import SweetAlert from "react-bootstrap-sweetalert";
// redux
import { useSelector } from "react-redux";
// rotas
import { useLocation } from "react-router-dom";
// services
import {
  getClickButtonMenu,
  deleteClickButtonMenu,
  getDashboardAccesses,
  getDashboardFeed,
  getDashboardRanking,
} from "../../../utils/services/dashboard";
import { getMe } from "../../../utils/services/loginUser";

export default function Dashboard() {
  const { token: jwt } = useSelector((state) => state.global_user_data.data);
  const {
    state: { idEvent: eventId },
  } = useLocation();

  // Dashboard data
  const [accesses, setAccesses] = useState({});
  const [feed, setFeed] = useState({});
  const [ranking, setRanking] = useState({});
  const [clickButtonMenu, setClickButtonMenu] = useState({});

  // limpeza dos cliques do menu — exclusiva do perfil master. É o mesmo
  // critério dos itens restritos do NavBar e do ícone de usuários na tela de
  // Eventos: compara o perfil do usuário logado com REACT_APP_PMCODE, e não
  // useUserPermissions, que liberaria para qualquer perfil padrão do contrato
  const [profileId, setProfileId] = useState("");
  const isMasterProfile = profileId === process.env.REACT_APP_PMCODE;

  const [clearClicksAlert, setClearClicksAlert] = useState(false);
  const [clearClicksSuccess, setClearClicksSuccess] = useState(false);
  const [clearClicksError, setClearClicksError] = useState(false);
  const [isClearingClicks, setIsClearingClicks] = useState(false);

  // Reports
  const [showRankingReport, setShowRankingReport] = useState(false);
  const [showAccessReport, setShowAccessReport] = useState(false);
  const [showFeedReport, setShowFeedReport] = useState(false);

  // fecha o relatório detalhado e volta para a visão geral do dashboard
  const backToDashboard = () => {
    setShowRankingReport(false);
    setShowAccessReport(false);
    setShowFeedReport(false);
  };

  // A api responde com chaves diferentes conforme o cache: `cachedData`
  // quando o Redis tem o valor, e o nome do dado (dashboardAccessesData,
  // dashboardFeedData, dashboardRankingData, analyticsButtonClick) quando
  // precisa calcular na hora. Ler só `cachedData` deixaria os gráficos
  // vazios na primeira visita, até o cache esquentar.
  function dashboardPayload(response, freshKey) {
    return response?.cachedData ?? response?.[freshKey] ?? {};
  }

  // busca os dados de ranking, feed, acessos e cliques do menu do dashboard
  const getDashboardData = useCallback(() => {
    const reqParams = { eventId, jwt };

    getDashboardRanking(reqParams)
      .then((response) => {
        setRanking(dashboardPayload(response, "dashboardRankingData"));
      })
      .catch((error) => {
        console.error("Erro ao buscar dados de ranking:", error);
        toast.error("Erro ao buscar dados de ranking.");
      });

    getDashboardFeed(reqParams)
      .then((response) => {
        setFeed(dashboardPayload(response, "dashboardFeedData"));
      })
      .catch((error) => {
        console.error("Erro ao buscar dados de feed:", error);
        toast.error("Erro ao buscar dados de feed.");
      });

    getDashboardAccesses(reqParams)
      .then((response) => {
        setAccesses(dashboardPayload(response, "dashboardAccessesData"));
      })
      .catch((error) => {
        console.error("Erro ao buscar dados de acessos:", error);
        toast.error("Erro ao buscar dados de acessos.");
      });
    getClickButtonMenu(reqParams)
      .then((response) => {
        setClickButtonMenu(dashboardPayload(response, "analyticsButtonClick"));
      })
      .catch((error) => {
        console.error("Erro ao buscar dados de cliques do menu:", error);
        toast.error("Erro ao buscar dados de cliques do menu.");
      });
  }, [eventId, jwt]);

  useEffect(() => {
    getDashboardData();
  }, [getDashboardData]);

  useEffect(() => {
    getProfile();
  }, []);

  // busca o ID do perfil do usuário logado
  async function getProfile() {
    const res = await getMe();
    if (res?.data?.id) {
      setProfileId(res.data.id);
    }
  }

  // apaga todos os cliques de menu do evento e recarrega o gráfico
  async function clearClicks() {
    setIsClearingClicks(true);

    try {
      const response = await deleteClickButtonMenu({ eventId });

      setClearClicksAlert(false);

      // o service devolve o corpo da resposta mesmo em erro, em vez de lançar;
      // deletedCount só vem quando a exclusão aconteceu
      if (response?.deletedCount >= 0) {
        setClearClicksSuccess(true);
      } else {
        setClearClicksError(true);
      }
    } catch (error) {
      setClearClicksAlert(false);
      setClearClicksError(true);
    } finally {
      setIsClearingClicks(false);
    }
  }

  return (
    <>
      {clearClicksAlert && (
        <SweetAlert
          showCancel
          warning
          title="Atenção"
          confirmBtnText={isClearingClicks ? "Limpando..." : "Sim"}
          cancelBtnText="Não"
          confirmBtnBsStyle="success"
          cancelBtnBsStyle="danger"
          disabled={isClearingClicks}
          onConfirm={() => clearClicks()}
          onCancel={() => setClearClicksAlert(false)}
        >
          {`Todos os ${clickButtonMenu?.total} cliques de menu deste evento serão apagados e não poderão ser recuperados. Deseja continuar?`}
        </SweetAlert>
      )}

      {clearClicksSuccess && (
        <SweetAlert
          success
          title="Sucesso"
          onConfirm={() => {
            setClearClicksSuccess(false);
            getDashboardData();
          }}
        >
          Cliques do menu apagados com sucesso!
        </SweetAlert>
      )}

      {clearClicksError && (
        <SweetAlert
          error
          title="Atenção"
          onConfirm={() => setClearClicksError(false)}
        >
          Não foi possível apagar os cliques do menu.
        </SweetAlert>
      )}

      {showAccessReport && (
        <div>
          <div className="back-to-dashboard">
            <i onClick={backToDashboard} className="bx bx-arrow-back" />
            <span onClick={backToDashboard}>Dashboard</span>
          </div>
          <Participants idEvent={eventId} reportToDashboard={true} dashAccess={true} />
        </div>
      )}
      {showRankingReport && (
        <div>
          <div className="back-to-dashboard">
            <i onClick={backToDashboard} className="bx bx-arrow-back" />
            <span onClick={backToDashboard}>Dashboard</span>
          </div>
          <Ranking idEvent={eventId} dashRanking={true} />
        </div>
      )}
      {showFeedReport && (
        <div>
          <div className="back-to-dashboard">
            <i onClick={backToDashboard} className="bx bx-arrow-back" />
            <span onClick={backToDashboard}>Dashboard</span>
          </div>
          <Feed idEvent={eventId} reportToDashboard={true} dashFeed={true} />
        </div>
      )}
      {!showRankingReport && !showAccessReport && !showFeedReport && (
        <div>
          <CardTitle style={{ marginBottom: 16 }}>Dashboard</CardTitle>
          <Row>
            <Col xs="12" lg="6" s="8">
              <Card className="card-shadow">
                <CardBody>
                  <PieChart data={accesses} title="Acessos" />
                  <div className="dashboard-access-footer">
                    <span style={{ color: "black", fontWeight: 700 }}>
                      Total de Usuários cadastrados:{" "}
                      {accesses.totalparticipants}
                    </span>
                    <Button onClick={() => setShowAccessReport(true)}>
                      <i className="bx bxs-report" />
                      <span>Relatório Detalhado</span>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xs="12" lg="6">
              <Card className="card-shadow">
                <CardBody>
                  <FeedChart feed={feed} />
                  <div className="dashboard-to-ranking">
                    <Button onClick={() => setShowFeedReport(true)}>
                      <i className="bx bxs-report" />
                      <span>Relatório Detalhado</span>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xs="12" lg="6">
              <Card className="card-shadow">
                <CardBody>
                  <RankChart ranking={ranking} />
                  <div className="dashboard-to-ranking">
                    <Button onClick={() => setShowRankingReport(true)}>
                      <i className="bx bxs-report" />
                      <span>Relatório Detalhado</span>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xs="12" lg="6">
              <Card className="card-shadow">
                <CardBody>
                  <DonutChart
                    data={clickButtonMenu?.data}
                    title="Cliques do Menu"
                    style={{
                      zIndex: 1,
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ color: "black", fontWeight: 700 }}>
                      Total de Cliques: {clickButtonMenu?.total}
                    </span>

                    {/* botão próprio, e não os de components/Buttons: aqueles
                    aplicam useUserPermissions e são feitos para ação de linha
                    de tabela. A ação também some sem cliques a apagar */}
                    {isMasterProfile && clickButtonMenu?.total > 0 && (
                      <Button
                        color="danger"
                        className="btn-rounded waves-effect waves-light"
                        onClick={() => setClearClicksAlert(true)}
                      >
                        <i className="bx bx-trash" />
                        <span> Limpar Todos Cliques</span>
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
}
